import { useTranslation } from '@omni/kit/hooks/useTranslation';
import { useCallback, useEffect, useRef } from 'react';

import { SettingsControlRef } from '../Controls/PlayerControlTypes/DefaultControls/components/SettingsControl/SettingsControl.types';
import { SubtitleTrack } from '../PlayerVideo/PlayerVideo.types';
import type {
  ToggleFullscreenUtility,
  UseFullscreenNativeChangeListener,
  UseFullscreenOrientationListener,
} from './fullscreenUtils';

const FULLSCREEN_EVENTS_WEB = [
  'fullscreenchange',
  'fullscreenerror',
  'mozfullscreenchange',
  'mozfullscreenerror',
  'msfullscreenchange',
  'msfullscreenerror',
  'webkitfullscreenchange',
  'webkitfullscreenerror',
];

const FULLSCREEN_EVENTS_IOS_WEB = [
  'webkitbeginfullscreen',
  'webkitendfullscreen',
];

export const toggleFullscreenUtility: ToggleFullscreenUtility = ({
  playerVideoRef,
  willFullscreen,
  callback,
}) => {
  const playerElement = playerVideoRef.current;

  if (willFullscreen) {
    playerElement.presentFullscreenPlayer();
    callback(true);
  } else {
    playerElement.dismissFullscreenPlayer();
    callback(false);
  }
};

export const useFullscreenOrientationListener: UseFullscreenOrientationListener =
  () => {
    /**
     * no-op for web.
     * Used in native to toggle fullscreen on orientation changing.
     */
  };

export const useFullscreenNativeChangeListener: UseFullscreenNativeChangeListener =
  (callback, playerControlsRef) => {
    const { t } = useTranslation();
    const savedCallback = useRef<typeof callback | null>(null);

    const updateFullscreen = useCallback(
      (event: Event) => {
        let isFullscreen = false;

        if (event.type.includes('fullscreenchange')) {
          isFullscreen =
            Boolean(document.fullscreenElement) ||
            // @ts-ignore
            Boolean(document.mozFullscreenElement) ||
            // @ts-ignore
            Boolean(document.msFullscreenElement);
        }

        callback(isFullscreen);
      },
      [callback]
    );

    /**
     * This callback is used to handle the playback rate change
     * for iOS web fullscreen mode.
     */
    const handleIOSFullscreenPlaybackRateChange = useCallback(() => {
      const videoElement = document.querySelector('video');

      if (!videoElement) return;

      const playbackSpeed = videoElement.playbackRate;

      const settingsControlRef =
        playerControlsRef?.current?.getSettingsControlRef() as SettingsControlRef;

      if (!settingsControlRef) return;

      settingsControlRef.handleSelectedPlaybackSpeed(playbackSpeed);
    }, [playerControlsRef]);

    /**
     * This callback is used to handle subtitle change
     * for iOS web fullscreen mode.
     */
    const handleIOSFullscreenSubtitleChange = useCallback(() => {
      const videoElement = document.querySelector('video');

      if (!videoElement) return;

      const textTracksArray = Array.from(videoElement.textTracks);
      const activeTrackIndex = textTracksArray.findIndex(
        (track) => track.mode === 'showing'
      );
      const activeTrack = textTracksArray[activeTrackIndex];

      const settingsControlRef =
        playerControlsRef?.current?.getSettingsControlRef() as SettingsControlRef;

      if (!settingsControlRef) return;

      if (activeTrackIndex !== -1 && activeTrack) {
        const subtitle = {
          name: activeTrack.label,
          lang: activeTrack.language,
        } as SubtitleTrack;

        settingsControlRef.handleSelectedSubtitleTrack(
          activeTrackIndex,
          subtitle
        );
      } else {
        settingsControlRef.handleSelectedSubtitleTrack(-1);
      }
    }, [playerControlsRef]);

    const updateFullscreenIosWeb = useCallback(() => {
      let isFullscreen = false;

      const videoElement = document.querySelector('video');

      // @ts-ignore
      if (videoElement?.webkitDisplayingFullscreen) {
        isFullscreen = true;
      }

      callback(isFullscreen);
    }, [callback]);

    useEffect(() => {
      savedCallback.current = callback;
    }, [callback]);

    /** hook up to ios web fullscreen events */
    useEffect(() => {
      const videoElement = document.querySelector('video');

      if (!videoElement) return;

      FULLSCREEN_EVENTS_IOS_WEB.forEach((type) => {
        videoElement.addEventListener(type, updateFullscreenIosWeb);
      });

      /**
       * Added two new listeneres to handle the playback rate change
       * and subtitle change for iOS web fullscreen mode.
       * These listeners are added to the video element instead of the document
       * because the properties are only available on the video element.
       */
      videoElement?.addEventListener(
        'ratechange',
        handleIOSFullscreenPlaybackRateChange
      );

      videoElement?.textTracks.addEventListener(
        'change',
        handleIOSFullscreenSubtitleChange
      );

      return () => {
        FULLSCREEN_EVENTS_IOS_WEB.forEach((type) => {
          videoElement.removeEventListener(type, updateFullscreenIosWeb);
        });

        videoElement.removeEventListener(
          'ratechange',
          handleIOSFullscreenPlaybackRateChange
        );

        videoElement.textTracks.removeEventListener(
          'change',
          handleIOSFullscreenSubtitleChange
        );
      };
    }, [
      handleIOSFullscreenPlaybackRateChange,
      handleIOSFullscreenSubtitleChange,
      updateFullscreenIosWeb,
    ]);

    /** hook up to web fullscreen events */
    useEffect(() => {
      FULLSCREEN_EVENTS_WEB.forEach((type) => {
        document.addEventListener(type, updateFullscreen);
      });

      return () => {
        FULLSCREEN_EVENTS_WEB.forEach((type) => {
          document.removeEventListener(type, updateFullscreen);
        });
      };
    }, [updateFullscreen]);
  };
